import { Button } from "react-bootstrap";
import { useHistory, useLocation } from "react-router-dom";
import TechnicalErorIcon from "@images/error/Technical_error_illustration.svg";
import errorBoundaryClient from "@apis/errorBoundaryClient";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";
import { useRef, ReactNode, ErrorInfo } from "react";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";

interface CustomErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorDetails {
  message: string;
  stack?: string;
  componentStack?: string | null;
}

const CustomErrorBoundary = ({ children }: CustomErrorBoundaryProps) => {
  const location = useLocation();

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={(error: Error, errorInfo: ErrorInfo) => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
          onErrorHandler(error, errorInfo, location.pathname + location.search);
        }, 1000);
      }}
    >
      {children}
    </ErrorBoundary>
  );
};

const ErrorFallback = (_props: FallbackProps) => {
  const history = useHistory();

  return (
    <div
      className="d-flex flex-column align-items-center justify-content-center"
      style={{ height: "100vh" }}
    >
      <div style={{ fontSize: "2rem" }}>Oooops</div>
      <div className="mb-3">{translate(LanguageKey.technical_error_info_key)}</div>
      <Button
        variant="sensolus-greylight"
        onClick={() => {
          history.push("/");
          window.location.reload();
        }}
      >
        {translate(LanguageKey.technical_error_back_home_key)}
      </Button>

      <img src={TechnicalErorIcon} alt="Technical error" />
    </div>
  );
};

const onErrorHandler = (error: Error, errorInfo: ErrorInfo, pathname: string) => {
  // Create a structured error log
  const errorDetails: ErrorDetails = {
    message: error.message + ". Url path: " + pathname,
    stack: error.stack,
    componentStack: errorInfo.componentStack,
    // You could include more context here, such as the current URL, user's id, etc.
  };

  // Send the error details to the server
  errorBoundaryClient
    .logError(errorDetails)
    .then(() => {})
    .catch(() => {});
};

export default CustomErrorBoundary;
